import React, { useEffect, useRef } from 'react';

/**
 * Particle System for Atmospheric Depth
 */
class Particle {
    x: number;
    y: number;
    size: number;
    speedY: number;
    life: number;
    maxLife: number;
    opacity: number;
    width: number;
    height: number;

    constructor(width: number, height: number) {
        this.width = width;
        this.height = height;
        this.x = 0;
        this.y = 0;
        this.size = 0;
        this.speedY = 0;
        this.life = 0;
        this.maxLife = 0;
        this.opacity = 0;
        this.reset();
    }

    reset() {
        this.x = Math.random() * this.width;
        this.y = Math.random() * this.height;
        this.size = Math.random() * 1.5;
        this.speedY = Math.random() * -0.5 - 0.1;
        this.life = 0;
        this.maxLife = Math.random() * 200 + 100;
        this.opacity = Math.random() * 0.5;
    }

    update() {
        this.y += this.speedY;
        this.life++;

        if (this.life > this.maxLife || this.y < 0) {
            this.reset();
            this.y = this.height + 10;
        }
    }

    draw(ctx: CanvasRenderingContext2D) {
        const fadeOpacity = this.opacity * (1 - this.life / this.maxLife);
        ctx.fillStyle = `rgba(200, 255, 240, ${fadeOpacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
    }
}

/**
 * THE PERFECT SMILE ENGINE
 * Uses physics-based harmonic oscillators to create a "silky ribbon" effect.
 */
// ... (imports)

const BackgroundAnimation: React.FC<{ className?: string }> = ({ className = "fixed inset-0" }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let width = container.clientWidth;
        let height = container.clientHeight;
        let time = 0;
        let animationFrameId: number;
        let isTabVisible = true;
        let isInViewport = true;

        // Ribbon Configuration
        const config = {
            strandCount: 40,      // Reduced slightly for performance
            verticalSpread: 180,
            smileCurve: 160,
            speed: 0.008,
            amplitude: 40,
            frequency: 0.002,
            bloomStrength: 15
        };

        let particles: Particle[] = [];

        const resize = () => {
            width = canvas.width = container.clientWidth;
            height = canvas.height = container.clientHeight;
            initParticles();
        };

        const initParticles = () => {
            particles = [];
            // Reduced particle count for performance
            const particleCount = window.innerWidth < 768 ? 20 : 50;
            for (let i = 0; i < particleCount; i++) {
                particles.push(new Particle(width, height));
            }
        };

        const animate = () => {
            // Only animate if tab is visible AND canvas is in viewport
            if (!isTabVisible || !isInViewport) {
                return;
            }

            // Clear but keep it high quality
            ctx.clearRect(0, 0, width, height);

            // 1. Draw Background Particles
            particles.forEach(p => {
                p.update();
                p.draw(ctx);
            });

            // 2. Setup Glowing Composite Mode
            ctx.globalCompositeOperation = 'lighter';

            // Move time forward
            time += config.speed;

            const cy = height * 0.55;

            // Draw the strands
            for (let i = 0; i < config.strandCount; i++) {
                const iNorm = i / config.strandCount;
                const hue = 160 + (iNorm * 30);
                const alpha = Math.sin(iNorm * Math.PI) * 0.3;

                ctx.beginPath();
                ctx.strokeStyle = `hsla(${hue}, 90%, 60%, ${alpha})`;
                ctx.lineWidth = 2;

                // Optimization: Increase step size for lower resolution screens if needed, 
                // but 15 is already decent.
                for (let x = 0; x < width; x += 15) {
                    const nx = (x / width) * 2 - 1;
                    const smileOffset = (nx * nx) * config.smileCurve;
                    const wave1 = Math.sin(x * config.frequency + time + (i * 0.1));
                    const wave2 = Math.cos(x * (config.frequency * 2) - time);
                    const waveY = (wave1 + wave2) * config.amplitude;
                    const strandOffset = (iNorm - 0.5) * config.verticalSpread;
                    const y = cy - smileOffset + waveY + strandOffset;

                    if (x === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.stroke();
            }

            ctx.globalCompositeOperation = 'source-over';

            // Optional: Draw a subtle gradient overlay
            const grad = ctx.createLinearGradient(0, 0, width, 0);
            grad.addColorStop(0, 'rgba(0,0,0,0)');
            grad.addColorStop(0.5, 'rgba(10, 255, 214, 0.05)');
            grad.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, width, height);

            animationFrameId = requestAnimationFrame(animate);
        };

        // Tab Visibility Handler
        const handleVisibilityChange = () => {
            isTabVisible = document.visibilityState === 'visible';
            if (isTabVisible && isInViewport) {
                cancelAnimationFrame(animationFrameId);
                animate();
            } else {
                cancelAnimationFrame(animationFrameId);
            }
        };

        // IntersectionObserver to pause when off-screen (CPU/GPU savings)
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                isInViewport = entry.isIntersecting;
                if (isInViewport && isTabVisible) {
                    cancelAnimationFrame(animationFrameId);
                    animate();
                } else {
                    cancelAnimationFrame(animationFrameId);
                }
            },
            { threshold: 0.1 } // Start animating when 10% visible
        );
        observer.observe(container);

        // Initialize
        window.addEventListener('resize', resize);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        resize();
        animate();

        return () => {
            window.removeEventListener('resize', resize);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            observer.disconnect();
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <div ref={containerRef} className={`z-0 pointer-events-none ${className}`}>
            {/* Cinematic Grain & Vignette */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                <div
                    className="absolute top-0 left-0 w-full h-full opacity-[0.05]"
                    style={{
                        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")"
                    }}
                />
                <div
                    className="absolute top-0 left-0 w-full h-full"
                    style={{
                        background: 'radial-gradient(circle at 50% 50%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.8) 100%)'
                    }}
                />
            </div>

            {/* Canvas */}
            <canvas
                ref={canvasRef}
                className="absolute top-0 left-0 w-full h-full z-0"
                style={{ filter: 'blur(0px)' }}
            />
        </div>
    );
};

export default BackgroundAnimation;
